"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildGraph = exports.buildDepGraphFromImportsAndModules = exports.getDepGraph = void 0;
const fs = require("fs");
const path = require("path");
const dep_graph_1 = require("@snyk/dep-graph");
const helpers_1 = require("./helpers");
const custom_error_1 = require("./errors/custom-error");
const version_1 = require("./version");
const sub_process_1 = require("./sub-process");
const package_url_1 = require("./package-url");
const component_metadata_1 = require("./component-metadata");
async function getDepGraph(root, targetFile, options = {}) {
    var _a, _b, _c;
    const { args: additionalArgs = [], configuration, includeComponentMetadata = false, } = options;
    const includeGoStandardLibraryDeps = (_a = configuration === null || configuration === void 0 ? void 0 : configuration.includeGoStandardLibraryDeps) !== null && _a !== void 0 ? _a : false;
    // Determine stdlib version
    const stdlibVersion = includeGoStandardLibraryDeps
        ? await (0, helpers_1.resolveStdlibVersion)(root, targetFile)
        : 'unknown';
    const useReplaceName = (_b = configuration === null || configuration === void 0 ? void 0 : configuration.useReplaceName) !== null && _b !== void 0 ? _b : false;
    const includePackageUrls = (_c = configuration === null || configuration === void 0 ? void 0 : configuration.includePackageUrls) !== null && _c !== void 0 ? _c : true;
    return buildDepGraphFromImportsAndModules(root, targetFile, {
        stdlibVersion,
        additionalArgs,
        includeGoStandardLibraryDeps,
        includePackageUrls,
        useReplaceName,
        includeComponentMetadata,
    });
}
exports.getDepGraph = getDepGraph;
async function buildDepGraphFromImportsAndModules(root = '.', targetFile = 'go.mod', options = {}) {
    var _a, _b;
    // TODO(BST-657): parse go.mod file to obtain root module name and go version
    const projectName = path.basename(root); // The correct name should come from the `go list` command
    const projectVersion = '0.0.0'; // TODO(BST-657): try `git describe`?
    options = {
        stdlibVersion: 'unknown',
        additionalArgs: [],
        includeGoStandardLibraryDeps: false,
        includePackageUrls: false,
        useReplaceName: false,
        includeComponentMetadata: false,
        ...options,
    };
    if (options.includeComponentMetadata) {
        options.goSumHashes = readGoSum(root, targetFile);
        options.goProxy = await readGoProxy(root, targetFile);
    }
    let rootPkg = createPkgInfo(projectName, projectVersion, options);
    let depGraphBuilder = new dep_graph_1.DepGraphBuilder({ name: 'gomodules' }, rootPkg);
    let goDepsOutput;
    const args = [
        'list',
        ...((_a = options.additionalArgs) !== null && _a !== void 0 ? _a : []),
        '-json',
        '-deps',
        './...',
    ];
    try {
        const goModAbsolutPath = path.resolve(root, path.dirname(targetFile));
        goDepsOutput = await (0, sub_process_1.runGo)(args, { cwd: goModAbsolutPath });
    }
    catch (err) {
        if (/cannot find main module, but found/.test(err)) {
            return depGraphBuilder.build();
        }
        if (/does not contain main module/.test(err)) {
            return depGraphBuilder.build();
        }
        const userError = new custom_error_1.CustomError(err);
        userError.userMessage = `'go ${args.join(' ')}' command failed with error: ${userError.message}`;
        throw userError;
    }
    if (goDepsOutput.includes('matched no packages')) {
        return depGraphBuilder.build();
    }
    const goDepsString = `[${goDepsOutput.replace(/}\r?\n{/g, '},{')}]`;
    const goDeps = JSON.parse(goDepsString);
    const packagesByName = {};
    for (const gp of goDeps) {
        packagesByName[normalizeImportPath(gp.ImportPath)] = gp;
    }
    const localPackages = goDeps.filter((gp) => !gp.DepOnly);
    const localPackageWithMainModule = localPackages.find((localPackage) => !!(localPackage.Module && localPackage.Module.Main));
    if ((_b = localPackageWithMainModule === null || localPackageWithMainModule === void 0 ? void 0 : localPackageWithMainModule.Module) === null || _b === void 0 ? void 0 : _b.Path) {
        rootPkg = createPkgInfo(localPackageWithMainModule.Module.Path, projectVersion, options);
        depGraphBuilder = new dep_graph_1.DepGraphBuilder({ name: 'gomodules' }, rootPkg);
    }
    const topLevelDeps = extractAllImports(localPackages);
    const childrenChain = new Map();
    const ancestorsChain = new Map();
    buildGraph(depGraphBuilder, topLevelDeps, packagesByName, 'root-node', childrenChain, ancestorsChain, options);
    return depGraphBuilder.build();
}
exports.buildDepGraphFromImportsAndModules = buildDepGraphFromImportsAndModules;
function buildGraph(depGraphBuilder, depPackages, packagesByName, currentParent, childrenChain, ancestorsChain, options, visited) {
    const depPackagesLen = depPackages.length;
    for (let i = depPackagesLen - 1; i >= 0; i--) {
        const localVisited = visited || new Set();
        const packageImport = depPackages[i];
        const version = 'unknown';
        // ---------- Standard library handling ----------
        if (isStandardLibraryPackage(packagesByName[packageImport])) {
            if (!options.includeGoStandardLibraryDeps) {
                continue; // skip when flag disabled
            }
            // All standard library packages are prefixed with "std/"
            const stdPackageName = `std/${packageImport}`;
            // create synthetic node and connect, then continue loop
            const stdNode = createPkgInfo(stdPackageName, options.stdlibVersion || version, options);
            depGraphBuilder.addPkgNode(stdNode, stdPackageName);
            depGraphBuilder.connectDep(currentParent, stdPackageName);
            continue;
        }
        // ---------- External package handling ----------
        const pkg = packagesByName[packageImport];
        if (!pkg || !pkg.DepOnly) {
            continue; // skip local or root-module packages
        }
        if (currentParent && packageImport) {
            const newNode = createPkgInfo(packageImport, version, options, pkg.Module);
            const componentLabels = getComponentLabelsForModule(pkg.Module, options);
            const currentChildren = childrenChain.get(currentParent) || [];
            const currentAncestors = ancestorsChain.get(currentParent) || [];
            const isAncestorOrChild = currentChildren.includes(packageImport) ||
                currentAncestors.includes(packageImport);
            // @TODO boost: breaking cycles,  re-work once dep-graph lib can handle cycles
            if (packageImport === currentParent || isAncestorOrChild) {
                continue;
            }
            if (localVisited.has(packageImport)) {
                const prunedId = `${packageImport}:pruned`;
                depGraphBuilder.addPkgNode(newNode, prunedId, {
                    labels: { pruned: 'true', ...componentLabels },
                });
                depGraphBuilder.connectDep(currentParent, prunedId);
                continue;
            }
            depGraphBuilder.addPkgNode(newNode, packageImport, Object.keys(componentLabels).length > 0
                ? { labels: componentLabels }
                : undefined);
            depGraphBuilder.connectDep(currentParent, packageImport);
            localVisited.add(packageImport);
            childrenChain.set(currentParent, [...currentChildren, packageImport]);
            ancestorsChain.set(packageImport, [...currentAncestors, currentParent]);
            const rawImports = packagesByName[packageImport].Imports || [];
            const transitives = [...new Set(rawImports.map(normalizeImportPath))];
            if (transitives.length > 0) {
                buildGraph(depGraphBuilder, transitives, packagesByName, packageImport, childrenChain, ancestorsChain, options, localVisited);
            }
        }
    }
}
exports.buildGraph = buildGraph;
// Read and parse the go.sum sitting alongside the target go.mod. A missing or
// unreadable go.sum is not fatal: component metadata is best-effort, so we
// return an empty map and carry on.
function readGoSum(root, targetFile) {
    const goSumPath = path.resolve(root, path.dirname(targetFile), 'go.sum');
    try {
        return (0, component_metadata_1.parseGoSum)(fs.readFileSync(goSumPath, 'utf8'));
    }
    catch {
        return {};
    }
}
// Resolve the effective GOPROXY via `go env GOPROXY`, which applies go's own
// precedence (env var > go env file > built-in default) — unlike reading
// process.env.GOPROXY, which misses values set with `go env -w`. Returns
// undefined if the lookup fails so URL derivation can fall back to the default.
async function readGoProxy(root, targetFile) {
    try {
        const cwd = path.resolve(root, path.dirname(targetFile));
        const out = await (0, sub_process_1.runGo)(['env', 'GOPROXY'], { cwd });
        return out.trim() || undefined;
    }
    catch {
        return undefined;
    }
}
// Resolve the component-metadata labels for the module a package belongs to.
// Returns {} unless the feature is enabled and we have a module. Uses the
// replacement module when present, since that is what actually gets downloaded
// and recorded in go.sum. The module hash is preferred from `go list`'s
// Module.Sum (go >= 1.23), falling back to the go.sum file for older toolchains
// that do not emit it. Both key on the raw (un-normalised) module version, so
// we deliberately avoid toSnykVersion here.
function getComponentLabelsForModule(goModule, options) {
    var _a;
    if (!options.includeComponentMetadata || !goModule) {
        return {};
    }
    const replace = goModule.Replace;
    const useReplaceInfo = (replace === null || replace === void 0 ? void 0 : replace.Path) && (replace === null || replace === void 0 ? void 0 : replace.Version);
    const modulePath = useReplaceInfo ? replace.Path : goModule.Path;
    const version = useReplaceInfo ? replace.Version : goModule.Version;
    if (!modulePath || !version) {
        return {};
    }
    const h1 = (useReplaceInfo ? replace.Sum : goModule.Sum) ||
        ((_a = options.goSumHashes) === null || _a === void 0 ? void 0 : _a[`${modulePath}@${version}`]);
    return (0, component_metadata_1.getComponentMetadataLabels)(modulePath, version, h1, options.goProxy);
}
function extractAllImports(goDeps) {
    const goDepsImports = new Set();
    for (const pkg of goDeps) {
        if (pkg.Imports) {
            for (const imp of pkg.Imports) {
                goDepsImports.add(normalizeImportPath(imp));
            }
        }
    }
    return Array.from(goDepsImports);
}
function isStandardLibraryPackage(pkgName) {
    // Go Standard Library Packages are marked as Standard: true
    return (pkgName === null || pkgName === void 0 ? void 0 : pkgName.Standard) === true;
}
// Normalize the ImportPath by stripping Go's variant annotations
// https://go.dev/doc/pgo
function normalizeImportPath(importPath) {
    return importPath.split(' ')[0];
}
function createPkgInfo(packageImport, version, options, goModule) {
    var _a, _b;
    const useReplaceInfo = ((_a = goModule === null || goModule === void 0 ? void 0 : goModule.Replace) === null || _a === void 0 ? void 0 : _a.Path) && ((_b = goModule === null || goModule === void 0 ? void 0 : goModule.Replace) === null || _b === void 0 ? void 0 : _b.Version);
    const includePurl = options.includePackageUrls && options.useReplaceName;
    /**
     * By default, the name is the full import path and the given version.
     */
    const pkg = {
        name: packageImport,
        version,
    };
    /**
     * If the module has any .Version information, parse and use it.
     */
    if (goModule === null || goModule === void 0 ? void 0 : goModule.Version) {
        pkg.version = (0, version_1.toSnykVersion)((0, version_1.parseVersion)(goModule.Version));
    }
    /**
     * If the module has a potential override with both .Path and .Version information,
     * use that information instead.
     */
    if (useReplaceInfo) {
        // Temporary: this is behind an option for controlled rollout and will
        // get cleaned up later.
        if (options.useReplaceName) {
            pkg.name = packageImport.replace(goModule.Path, goModule.Replace.Path);
        }
        pkg.version = (0, version_1.toSnykVersion)((0, version_1.parseVersion)(goModule.Replace.Version));
    }
    /**
     * If no purls are being included, stop here.
     */
    if (!includePurl) {
        return pkg;
    }
    if (useReplaceInfo) {
        // Create purl from replaced module only if both .Name and .Version are present.
        pkg.purl = (0, package_url_1.createGoPurl)(goModule.Replace, pkg);
    }
    else if (goModule) {
        // Otherwise use the original module.
        pkg.purl = (0, package_url_1.createGoPurl)(goModule, pkg);
    }
    else {
        // Or if there is none, create a purl from the import path and given version.
        pkg.purl = (0, package_url_1.createGoPurl)({ Path: packageImport, Version: version });
    }
    return pkg;
}
//# sourceMappingURL=dep-graph.js.map